import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  SummarizeOutlined as SummarizeIcon
} from '@mui/icons-material';
import {
  TemplateSummary,
  CreateTemplateSummaryInput,
  UpdateTemplateSummaryInput
} from '@/types/templateSummaryTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { DistributionGroup } from '@/types/distributionGroupTypes';
import { getDistributionGroups } from '@/services/distributionGroupService';
import { DistributionGroupManager } from './DistributionGroupManager';
import { AttioTextField } from '@/components/AttioForm';

export interface SummaryListRef {
  openAddDialog: () => void;
}

interface SummaryListProps {
  templateId: number;
  summaries: TemplateSummary[];
  onAddSummary: (summary: CreateTemplateSummaryInput) => Promise<void>;
  onUpdateSummary: (summary: UpdateTemplateSummaryInput) => Promise<void>;
  onDeleteSummary: (summaryId: number) => Promise<void>;
  isLoading?: boolean;
  companyId?: string;
  userId?: string;
  saveStatus?: 'idle' | 'saving' | 'success' | 'error';
  onSummaryGroupsUpdated?: (
    summaryId: number,
    groups: DistributionGroup[]
  ) => void;
}

interface SummaryFormData {
  name: string;
  description: string;
  prompt: string;
  isEnabled: boolean;
  selectedDistributionGroupIds: number[];
}

const defaultSummaryForm: SummaryFormData = {
  name: '',
  description: '',
  prompt: '',
  isEnabled: true,
  selectedDistributionGroupIds: []
};

// Summary Item Component
interface SummaryItemProps {
  summary: TemplateSummary;
  onEdit: () => void;
  onDelete: () => void;
}

const SummaryItem: React.FC<SummaryItemProps> = ({
  summary,
  onEdit,
  onDelete
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit();
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete();
  };

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        mb: 2,
        boxShadow: 'none',
        '&:hover': {
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '4px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                color: '#6b7280',
                flexShrink: 0
              }}
            >
              <SummarizeIcon sx={{ fontSize: 16 }} />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 0.5
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: 'text.primary',
                    lineHeight: 1.4,
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {summary.name}
                </Typography>

                {!summary.isEnabled && (
                  <Chip
                    label="Disabled"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '11px',
                      fontWeight: 500,
                      color: 'text.disabled',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                    }}
                  />
                )}

                {summary.distributionGroups && summary.distributionGroups.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.75,
                      py: 0.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '6px',
                      height: 20,
                      color: 'text.secondary',
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 13 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '11px',
                        lineHeight: 1,
                        fontWeight: 500
                      }}
                    >
                      {summary.distributionGroups.length}
                    </Typography>
                  </Box>
                )}
              </Box>

              {summary.description && (
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: '12px',
                    lineHeight: 1.5,
                    textAlign: 'left'
                  }}
                >
                  {summary.description}
                </Typography>
              )}
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              color: 'text.secondary',
              flexShrink: 0,
              '&:hover': {
                color: 'text.primary',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 160,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.5)'
              : '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{ color: 'error' }}
          />
        </MenuItem>
      </Menu>
    </Card>
  );
};

export const SummaryList = forwardRef<SummaryListRef, SummaryListProps>(({
  templateId,
  summaries,
  onAddSummary,
  onUpdateSummary,
  onDeleteSummary,
  isLoading = false,
  companyId,
  userId,
  saveStatus = 'idle',
  onSummaryGroupsUpdated
}, ref) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSummary, setEditingSummary] = useState<TemplateSummary | null>(
    null
  );
  const [formData, setFormData] = useState<SummaryFormData>(defaultSummaryForm);

  // Distribution groups for dialog
  const [availableGroups, setAvailableGroups] = useState<DistributionGroup[]>(
    []
  );
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const isSaving = saveStatus === 'saving';

  // Expose method to open add dialog
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog()
  }));

  const handleOpenDialog = async (summary?: TemplateSummary) => {
    if (summary) {
      setEditingSummary(summary);
      // Use distribution groups already included in the summary object
      setFormData({
        name: summary.name,
        description: summary.description,
        prompt: summary.prompt,
        isEnabled: summary.isEnabled,
        selectedDistributionGroupIds: (summary.distributionGroups || []).map(
          (g) => g.id
        )
      });
    } else {
      setEditingSummary(null);
      setFormData(defaultSummaryForm);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSummary(null);
    setFormData(defaultSummaryForm);
    setAvailableGroups([]);
    setGroupsError(null);
  };

  // Load available distribution groups when dialog opens
  useEffect(() => {
    const loadAvailableGroups = async () => {
      if (!dialogOpen || !companyId) return;

      setIsLoadingGroups(true);
      setGroupsError(null);

      try {
        const groups = await getDistributionGroups(
          companyId,
          () => getAccessTokenSilently() as Promise<string>,
          () => getAccessTokenWithPopup() as Promise<string>
        );
        setAvailableGroups(groups);
      } catch (error: any) {
        setGroupsError(error.message || 'Failed to load distribution groups');
        console.error('Error loading distribution groups:', error);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    loadAvailableGroups();
  }, [dialogOpen, companyId, getAccessTokenSilently, getAccessTokenWithPopup]);

  const handleToggleDistributionGroup = (groupId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedDistributionGroupIds: prev.selectedDistributionGroupIds.includes(
        groupId
      )
        ? prev.selectedDistributionGroupIds.filter((id) => id !== groupId)
        : [...prev.selectedDistributionGroupIds, groupId]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.prompt.trim()) return;

    try {
      if (editingSummary) {
        // Update existing summary including distribution groups
        await onUpdateSummary({
          id: editingSummary.id,
          name: formData.name,
          description: formData.description,
          prompt: formData.prompt,
          isEnabled: formData.isEnabled,
          distributionGroupIds: formData.selectedDistributionGroupIds
        });
      } else {
        // Create new summary
        const nextOrder = Math.max(0, ...summaries.map((s) => s.order)) + 1;
        await onAddSummary({
          templateId,
          name: formData.name,
          description: formData.description,
          prompt: formData.prompt,
          isEnabled: formData.isEnabled,
          order: nextOrder,
          distributionGroupIds: formData.selectedDistributionGroupIds
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving summary:', error);
    }
  };

  return (
    <Box>
      {/* Summary List */}
      {summaries.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'text.primary',
              mb: 1.5
            }}
          >
            No summaries defined
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              lineHeight: 1.6,
              maxWidth: '400px',
              mx: 'auto'
            }}
          >
            Define how summaries should be generated for different audiences
            after conversations end. Click "New Summary" to get started.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 4, pb: 3, pt: 3 }}>
          <Stack spacing={2}>
            {summaries.map((summary) => (
              <SummaryItem
                key={summary.id}
                summary={summary}
                onEdit={() => handleOpenDialog(summary)}
                onDelete={() => onDeleteSummary(summary.id)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSummary
            ? 'Edit Summary Definition'
            : 'Add Summary Definition'}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}>
            <AttioTextField
              label="Summary Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              disabled={isSaving}
              placeholder="e.g., Legal Team Summary, Public Report"
            />

            <AttioTextField
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value
                }))
              }
              multiline
              rows={2}
              disabled={isSaving}
              placeholder="Describe what this summary is for and who will receive it"
            />

            <AttioTextField
              label="AI Prompt"
              value={formData.prompt}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, prompt: e.target.value }))
              }
              multiline
              minRows={3}
              maxRows={15}
              required
              disabled={isSaving}
              placeholder="Enter the prompt that will be used to generate this summary..."
              description="This prompt will be sent to the AI along with the conversation transcript to generate the summary"
            />

            {/* Distribution Groups Selection */}
            <Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 0.75,
                  display: 'block'
                }}
              >
                Distribution Groups
              </Typography>
              {isLoadingGroups ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CircularProgress size={18} />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: theme.palette.text.secondary
                    }}
                  >
                    Loading groups...
                  </Typography>
                </Box>
              ) : groupsError ? (
                <Alert severity="error">
                  {groupsError}
                </Alert>
              ) : availableGroups.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: theme.palette.text.secondary
                  }}
                >
                  No distribution groups available. Create distribution groups
                  first in the Distribution Groups section.
                </Typography>
              ) : (
                <Box>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.text.secondary,
                      mb: 1.5,
                      lineHeight: 1.4
                    }}
                  >
                    Select which distribution groups should receive this
                    summary:
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                  >
                    {availableGroups.map((group) => (
                      <Chip
                        key={group.id}
                        label={group.name}
                        onClick={() => handleToggleDistributionGroup(group.id)}
                        variant={
                          formData.selectedDistributionGroupIds.includes(
                            group.id
                          )
                            ? 'filled'
                            : 'outlined'
                        }
                        color={
                          formData.selectedDistributionGroupIds.includes(
                            group.id
                          )
                            ? 'primary'
                            : 'default'
                        }
                        size="small"
                        disabled={!group.isActive || isSaving}
                        sx={{
                          cursor:
                            group.isActive && !isSaving ? 'pointer' : 'default',
                          opacity: group.isActive && !isSaving ? 1 : 0.5,
                          borderRadius: '6px'
                        }}
                      />
                    ))}
                  </Box>
                  {formData.selectedDistributionGroupIds.length > 0 && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: theme.palette.text.secondary,
                        mt: 1
                      }}
                    >
                      {formData.selectedDistributionGroupIds.length} group
                      {formData.selectedDistributionGroupIds.length !== 1
                        ? 's'
                        : ''}{' '}
                      selected
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <Box 
              sx={{ 
                pt: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                px: 2,
                py: 1.5,
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                }}
              >
                Enable this summary for automatic generation
              </Typography>
              <Switch
                checked={formData.isEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isEnabled: e.target.checked
                  }))
                }
                disabled={isSaving}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              isSaving || !formData.name.trim() || !formData.prompt.trim()
            }
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {isSaving
              ? editingSummary
                ? 'Updating...'
                : 'Creating...'
              : (editingSummary ? 'Update' : 'Create') + ' Summary'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
